import { YesNoIcon } from 'components'
import { DownloadLink } from 'components/DownloadLink'
import {
  ColumnRenderer,
  EntityList,
  EntityListProps,
  renderDate,
  renderEnum,
  renderPersona,
} from 'components/EntityList'
import { Box } from 'grid'
import { Participant } from 'model'
import {
  ConstrainMode,
  Icon,
  PersonaSize,
  SelectionMode,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'
export type FormListProps = EntityListProps<Partial<Participant>>

export const FormList: React.FunctionComponent<FormListProps> = ({
  items,
  ...props
}) => {
  const { t } = useTranslation()

  const renderFormIcons: ColumnRenderer = (item: Participant, i, col) => (
    <Box px={1}>
      <YesNoIcon
        value={!!item[col!.fieldName!]}
        style={{ marginRight: 4 }}
        title={t('model.proving.form.formActive')}
      />
      {item.forms && item.forms[col!.fieldName!] && !!item[col!.fieldName!] ? (
        <DownloadLink uri={item.forms[col!.fieldName!].uri}>
          <Icon iconName={'Save'} title={t('model.proving.form.formCreated')} />
        </DownloadLink>
      ) : (
        !!item[col!.fieldName!] && (
          <Icon
            iconName={'HourGlass'}
            title={t('model.proving.form.formNotYetCreated')}
          />
        )
      )}
    </Box>
  )

  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])

  const columns = [
    fieldColumn('person.type', {
      minWidth: 120,
      isGrouped: true,
      getGroupValue: (item: Participant) =>
        item.unknownPerson
          ? t(`enum.personType.${item.unknownPerson.personType}`)
          : t(`enum.personType.${item.person!.type}`),
      onRender: (item: Participant, i, col) =>
        item.unknownPerson
          ? t(`enum.personType.${item.unknownPerson.personType}`)
          : renderEnum(t, 'personType')(item, i, col),
      useFullFieldname: true,
    }),
    fieldColumn('person', {
      onRender: (item: Participant, i, col) => {
        if (item.unknownPerson && item.unknownPerson.isUnknown) {
          return t('model.proving.participant.participantUnknown')
        } else {
          return renderPersona({
            size: PersonaSize.size10,
          })(item, i, col)
        }
      },
      minWidth: 155,
      ungroupable: true,
    }),
    fieldColumn('dateFrom', { onRender: renderDate, ungroupable: true }),
    fieldColumn('dateTo', { onRender: renderDate, ungroupable: true }),
    fieldColumn('temporaryPass', {
      minWidth: 105,
      onRender: renderFormIcons,
      ungroupable: true,
    }),
    fieldColumn('passActivation', {
      minWidth: 115,
      onRender: renderFormIcons,
      ungroupable: true,
    }),
    fieldColumn('permanentDriveAuthorization', {
      minWidth: 125,
      ungroupable: true,
      onRender: renderFormIcons,
    }),
    fieldColumn('singleDriveAuthorization', {
      minWidth: 125,
      ungroupable: true,
      onRender: renderFormIcons,
    }),
    fieldColumn('dvEquipment', {
      ungroupable: true,
      onRender: renderFormIcons,
    }),
    fieldColumn('partsTransport', {
      ungroupable: true,
      onRender: renderFormIcons,
    }),
    fieldColumn('driverTraining', {
      ungroupable: true,
      onRender: renderFormIcons,
    }),
  ]

  return (
    <EntityList
      alternate={true}
      stickyHeader={true}
      selectionMode={SelectionMode.multiple}
      items={items}
      constrainMode={ConstrainMode.horizontalConstrained}
      columns={columns}
      {...props}
    />
  )
}
